import React, { useState, useRef, useEffect } from 'react';
import './SkillCard.css';
import SkillDescriptionPopup from './SkillDescriptionPopup';

const HoldIndicator = ({ percent, size = 54 }) => (
    <svg
        width={size}
        height={size}
        style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            pointerEvents: "none",
            zIndex: 10,
            transform: `translate(-50%, -50%)`
        }}
    >
        <circle
            cx={size/2}
            cy={size/2}
            r={size/2 - 6}
            stroke="#ecc13b"
            strokeWidth="4"
            fill="rgba(38,35,52,0.10)"
        />
        <circle
            cx={size/2}
            cy={size/2}
            r={size/2 - 6}
            stroke="#ecc13b"
            strokeWidth="4"
            fill="none"
            strokeDasharray={2*Math.PI*(size/2-6)}
            strokeDashoffset={2*Math.PI*(size/2-6) * (1 - percent)}
            style={{ transition: percent ? 'stroke-dashoffset 0.05s linear' : undefined }}
        />
    </svg>
);

const LONG_PRESS_MS = 500;
const HOVER_DELAY_MS = 500;     // indicator fill duration
const HOVER_GRACE_MS = 1000;    // wait before indicator appears

const SkillCard = ({
                       skill,
                       spCost,
                       getSkillCost,
                       onIncrement,
                       onDecrement,
                       onRemove,
                       description
                   }) => {
    const canDecrement = skill.primaryBaseCost !== 0 || skill.skillset !== 8;
    const isSinglePurchase = skill && !(skill.incrementCost > 0);

    const refund = getSkillCost(skill) - getSkillCost(skill, skill.ranks - 1);
    const increment = !isSinglePurchase
        ? (getSkillCost(skill, skill.ranks + 1) - getSkillCost(skill))
        : 0;
    const incrementLabel = !isSinglePurchase ? `(-${increment})` : '(Max)';

    const [showPopup, setShowPopup] = useState(false);
    const [popupTrigger, setPopupTrigger] = useState(null);
    const [popupPos, setPopupPos] = useState(null);

    // Shared progress for hover/touch indicators
    const [holdProgress, setHoldProgress] = useState(0);
    const progressKindRef = useRef(''); // 'hover' | 'touch' | ''

    const cardRef = useRef();
    const nameRef = useRef();

    // Touch (long-press)
    const longPressTimeout = useRef();
    const rafRef = useRef();
    const touchStartTime = useRef();

    // Hover
    const hoverGraceTimeout = useRef();
    const hoverOpenTimeout = useRef();
    const hoverStartTime = useRef();
    const hoverRafRef = useRef();
    const hoveringRef = useRef(false);

    // Auto-fit font size for skill-name
    const adjustFontSize = () => {
        const el = nameRef.current;
        if (!el) return;
        el.style.fontSize = '';
        const fontSize = parseFloat(getComputedStyle(el).fontSize);
        const minFontSize = 10;

        if (el.scrollWidth > el.clientWidth) {
            const scale = el.clientWidth / el.scrollWidth;
            const newSize = Math.max(minFontSize, fontSize * scale);
            el.style.fontSize = newSize + 'px';
        }
    };

    useEffect(() => {
        adjustFontSize();
        window.addEventListener('resize', adjustFontSize);
        return () => {
            window.removeEventListener('resize', adjustFontSize);
        };
    }, [skill.name, spCost]);

    const isControlButton = (el) => {
        if (!el) return false;
        return el.closest('.control-btn') !== null;
    };

    const clearHoverTimers = () => {
        clearTimeout(hoverGraceTimeout.current);
        clearTimeout(hoverOpenTimeout.current);
        cancelAnimationFrame(hoverRafRef.current);
    };

    // Desktop: hover with grace + progress (opens tooltip)
    const handleMouseEnter = (e) => {
        if (isControlButton(e.target)) return;

        // Reset/prepare
        hoveringRef.current = true;
        clearHoverTimers();
        setHoldProgress(0);

        // After grace period, show indicator and start filling
        hoverGraceTimeout.current = setTimeout(() => {
            if (!hoveringRef.current) return;

            progressKindRef.current = 'hover';
            setHoldProgress(0);
            hoverStartTime.current = performance.now();

            const animate = () => {
                const elapsed = performance.now() - hoverStartTime.current;
                const percent = Math.min(elapsed / HOVER_DELAY_MS, 1);
                setHoldProgress(percent);
                if (percent < 1 && hoveringRef.current) {
                    hoverRafRef.current = requestAnimationFrame(animate);
                }
            };
            hoverRafRef.current = requestAnimationFrame(animate);

            // When full, open popup
            hoverOpenTimeout.current = setTimeout(() => {
                if (!hoveringRef.current) return;
                const rect = cardRef.current?.getBoundingClientRect();
                setPopupPos(rect ? { x: rect.right + 12, y: rect.top + rect.height / 2 } : null);
                setShowPopup(true);
                setPopupTrigger('hover');
                setHoldProgress(0);
                progressKindRef.current = '';
            }, HOVER_DELAY_MS);
        }, HOVER_GRACE_MS);
    };

    const handleMouseLeave = () => {
        hoveringRef.current = false;
        clearHoverTimers();
        setHoldProgress(0);
        progressKindRef.current = '';

        // IMPORTANT: Do NOT auto-close a hover-opened popup here.
        // Many popups render an overlay that can cause an immediate mouseleave,
        // which would instantly close it. Let the popup manage its own onClose.
    };

    // Desktop: right-click (context menu)
    const handleContextMenu = (e) => {
        if (isControlButton(e.target)) return;
        e.preventDefault();
        hoveringRef.current = false;
        clearHoverTimers();
        setHoldProgress(0);
        progressKindRef.current = '';

        const { clientX, clientY } = e;
        setPopupPos({ x: clientX, y: clientY });
        setShowPopup(true);
        setPopupTrigger('context');
    };

    // Mobile: long tap only
    const handleTouchStart = (e) => {
        if (isControlButton(e.target)) return;

        // Cancel any hover flow
        hoveringRef.current = false;
        clearHoverTimers();

        touchStartTime.current = performance.now();
        progressKindRef.current = 'touch';
        setHoldProgress(0);

        const animate = () => {
            const elapsed = performance.now() - touchStartTime.current;
            const percent = Math.min(elapsed / LONG_PRESS_MS, 1);
            setHoldProgress(percent);
            if (percent < 1) {
                rafRef.current = requestAnimationFrame(animate);
            }
        };
        rafRef.current = requestAnimationFrame(animate);

        longPressTimeout.current = setTimeout(() => {
            setPopupPos({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
            setShowPopup(true);
            setPopupTrigger('touch');
            setHoldProgress(0);
            progressKindRef.current = '';
        }, LONG_PRESS_MS);
    };

    const clearLongPress = () => {
        clearTimeout(longPressTimeout.current);
        cancelAnimationFrame(rafRef.current);
        setHoldProgress(0);
        progressKindRef.current = '';
    };

    return (
        <>
            <div
                ref={cardRef}
                className="skill-card"
                onContextMenu={handleContextMenu}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchEnd={clearLongPress}
                onTouchCancel={clearLongPress}
                onTouchMove={clearLongPress}
                style={{ position: 'relative' }}
            >
                {/* Indicator:
                    - hover: small, top-right (via wrapper CSS)
                    - touch: centered (original) */}
                {holdProgress > 0 && holdProgress < 1 && (
                    progressKindRef.current === 'hover' ? (
                        <div className="hover-indicator">
                            <HoldIndicator percent={holdProgress} size={20} />
                        </div>
                    ) : (
                        <HoldIndicator percent={holdProgress} size={54} />
                    )
                )}

                <div className="skill-line">
                    <div className="skill-name" ref={nameRef}>
                        {skill.name || skill.skillId}
                    </div>
                    <div className="skill-controls-horizontal">
                        <div className="skill-rank-controls">
                            <span className="skill-ranks">{skill.ranks}</span>
                            <div className="skill-cost">({spCost} SP)</div>
                            <button
                                type="button"
                                className="control-btn decrement-btn"
                                onClick={(e) => onDecrement(skill.skillId, e)}
                                disabled={!canDecrement}
                            >
                                <span className="control-btn-down-arrow">↓</span> (+{refund})
                            </button>
                            <button
                                type="button"
                                className="control-btn increment-btn"
                                onClick={(e) => onIncrement(skill.skillId, e)}
                                disabled={isSinglePurchase}
                                title={isSinglePurchase ? "This is a single-purchase skill. Max rank reached." : ""}
                            >
                                <span className="control-btn-down-arrow">↑</span> {incrementLabel}
                            </button>
                        </div>
                    </div>
                </div>

                {skill.prereqs && Array.isArray(skill.prereqs) && skill.prereqs.length > 0 && (
                    <div className="skill-prereqs">
                        <strong>Prerequisites:</strong>
                        <ul>
                            {skill.prereqs.map(pr => (
                                <li key={pr.prereqSkillId}>
                                    {pr.name} (Requires {pr.ranks} {pr.ranks > 1 ? 'ranks' : 'rank'})
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {showPopup && (
                <SkillDescriptionPopup
                    skill={{
                        ...skill,
                        cost: spCost,
                        description: description ?? skill.description
                    }}
                    position={popupPos}
                    onClose={() => {
                        setShowPopup(false);
                        setPopupTrigger(null);
                    }}
                />
            )}
        </>
    );
};

export default SkillCard;
